/**
 * RoutingDecisionCard — surfaces the Decision Routing Layer.
 *
 * Server is the single source of truth (GET /quotations/{id}/routing).
 * Renders the BASE tier → FINAL tier with every escalation reason
 * spelled out — so the CFO knows *why* a request landed in their queue.
 * Hidden when no base tier matches (auto-approved) AND no escalation fires.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { ArrowUpRight, GitFork } from 'lucide-react';

export default function RoutingDecisionCard({ quotation }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!quotation?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: res } = await axiosInstance.get(
          `/quotations/${quotation.id}/routing`
        );
        if (!cancelled) setData(res);
      } catch (_) {
        if (!cancelled) setData(null);
      }
    })();
    return () => { cancelled = true; };
  }, [quotation?.id]);

  if (!data) return null;
  // Auto-approved + no escalation → completely silent (avoid banner noise).
  if (!data.base_tier && !data.escalated) return null;

  const baseLabel  = data.base_tier?.label  || 'Auto-approved';
  const finalLabel = data.final_tier?.label || 'Auto-approved';

  return (
    <div
      data-testid="routing-decision-card"
      className={`mb-3 rounded-lg border p-3 text-sm ${
        data.escalated
          ? 'border-orange-200 bg-orange-50 text-orange-900'
          : 'border-gray-200 bg-gray-50 text-gray-700'
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <GitFork className="w-4 h-4" />
        <span className="font-semibold">Decision Routing</span>
        {data.escalated && (
          <span
            data-testid="routing-escalated-chip"
            className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-orange-100 border border-orange-300 text-orange-800"
          >
            <ArrowUpRight className="w-3 h-3" /> Escalated
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs mb-2">
        <span
          data-testid="routing-base-tier"
          className="px-2 py-0.5 rounded bg-white border border-current/30 font-mono"
        >
          Base: {baseLabel}
        </span>
        {data.escalated && (
          <>
            <ArrowUpRight className="w-3 h-3 opacity-60" />
            <span
              data-testid="routing-final-tier"
              className="px-2 py-0.5 rounded bg-white border border-orange-400 font-mono font-semibold"
            >
              Final: {finalLabel}
            </span>
          </>
        )}
      </div>

      {data.escalations.length > 0 ? (
        <ul
          data-testid="routing-escalation-list"
          className="space-y-1 text-xs"
        >
          {data.escalations.map((esc, i) => {
            const sv = esc.signal_key === 'discount_pct'
              ? `${(esc.signal_value * 100).toFixed(1)}%`
              : esc.signal_key === 'collection_prob'
              ? `${(esc.signal_value * 100).toFixed(0)}%`
              : String(esc.signal_value);
            return (
              <li
                key={i}
                data-testid={`routing-rule-${esc.rule_id}`}
                className="flex items-center gap-2"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50 flex-shrink-0" />
                <span className="font-medium">{esc.label}:</span>
                <span className="font-mono opacity-80">{sv}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="text-xs opacity-80">{data.explanation}</div>
      )}
    </div>
  );
}
